"use client";

import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { Category, Subcategory } from "../types/public.types";
import { publicService } from "../services/publicService";

interface PublicContextType {
  categories: Category[];
  subcategories: Subcategory[];
  loadingCategories: boolean;
  loadingSubcategories: boolean;
  error: string | null;
  refreshCategories: () => Promise<void>;
  refreshSubcategories: () => Promise<void>;
}

const PublicContext = createContext<PublicContextType | undefined>(undefined);

export const PublicProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSubcategories, setLoadingSubcategories] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const data = await publicService.getCategories();
      setCategories(data);
    } catch (err: any) {
      console.error("Failed to load public categories", err);
      setError(err.message || "Failed to load categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      setLoadingSubcategories(true);
      const data = await publicService.getSubcategories();
      setSubcategories(data);
    } catch (err: any) {
      console.error("Failed to load public subcategories", err);
      setError(err.message || "Failed to load subcategories");
    } finally {
      setLoadingSubcategories(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
  }, []);

  const contextValue = useMemo(() => ({
    categories,
    subcategories,
    loadingCategories,
    loadingSubcategories,
    error,
    refreshCategories: fetchCategories,
    refreshSubcategories: fetchSubcategories,
  }), [categories, subcategories, loadingCategories, loadingSubcategories, error]);

  return (
    <PublicContext.Provider value={contextValue}>
      {children}
    </PublicContext.Provider>
  );
};

export const usePublicContext = () => {
  const context = useContext(PublicContext);
  if (!context) {
    throw new Error("usePublicContext must be used within a PublicProvider");
  }
  return context;
};
